import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defaultAxiosInstance } from "../api/defaultInstance";
import "../css/AdProductDetail.css";
import Layout from "./Layout";

function AdProductDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { metaProduct } = location.state || { metaProduct: "reward" };

  const query = new URLSearchParams(location.search);
  const adProductId = query.get("adProductId");

  const [product, setProduct] = useState(null);
  const [formData, setFormData] = useState({
    advertiserName: "",
    adName: "",
    productType: "",
    adDescription: "",
    price: "",
    unit: "",
    averageClicks: "",
    exposureMedia: [],
    exposurePosition: "",
    minQuantity: "",
    minPeriod: "",
    maxPeriod: "",
    mediaName: "",
    mediaUrl: "",
    categoryPrices: [],
    startPrice: "",
    isGhostwriting: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    if (adProductId) {
      fetchProductDetail();
    }
  }, [adProductId]);

  useEffect(() => {
    if (metaProduct === "news_release") {
      defaultAxiosInstance
        .get("/advertisement/admin/newsRelease/categories")
        .then((res) => {
          setCategoryOptions(res.data.data || []);
        });
    }
  }, [metaProduct]);

  const fetchProductDetail = async () => {
    try {
      const response = await defaultAxiosInstance.get(
        "/advertisement/admin/product/id",
        {
          params: { adProductId },
        },
      );
      const result = response.data.data;
      setProduct(result);
      setFormData({
        advertiserName: result.advertiserName,
        adName: result.adName,
        productType: result.productType,
        adDescription: result.adDescription,
        price: result.price,
        unit: result.unit,
        averageClicks: result.averageClicks,
        exposureMedia: Array.isArray(result.exposureMedia)
          ? result.exposureMedia
          : [],
        exposurePosition: Array.isArray(result.exposurePosition)
          ? result.exposurePosition.join(", ")
          : "",
        minQuantity: result.minQuantity,
        minPeriod: result.minPeriod,
        maxPeriod: result.maxPeriod,
        mediaName: result.mediaName,
        mediaUrl: result.mediaUrl,
        categoryPrices: result.categoryPrices || [],
        startPrice: result.startPrice,
        isGhostwriting: result.isGhostwriting === 1,
      });
    } catch (error) {
      console.error("광고 상품 상세 조회 오류:", error);
      alert("광고 상품 상세 조회 오류");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "categories" || name === "exposureMedia") {
      setFormData((prev) => ({
        ...prev,
        [name]: value.split(",").map((item) => item.trim()),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryPriceChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedCategoryPrices = prev.categoryPrices.map((item, i) => {
        if (i !== index) return item;
        if (field === "categoryId") {
          const selectedCategory = categoryOptions.find(
            (opt) => String(opt.categoryId) === String(value),
          );
          return {
            ...item,
            categoryId: value,
            category: selectedCategory || null,
          };
        }
        return { ...item, [field]: value };
      });
      // 시작 금액 자동 계산
      const startPrice =
        updatedCategoryPrices.length > 0
          ? Math.min(
              ...updatedCategoryPrices.map((cp) => Number(cp.price) || 0),
            )
          : 0;
      return {
        ...prev,
        categoryPrices: updatedCategoryPrices,
        startPrice,
      };
    });
  };

  const handleAddCategoryPrice = () => {
    setFormData((prev) => ({
      ...prev,
      categoryPrices: [
        ...prev.categoryPrices,
        { categoryId: "", price: "", category: null },
      ],
    }));
  };

  const handleRemoveCategoryPrice = (index) => {
    setFormData((prev) => {
      const updatedCategoryPrices = prev.categoryPrices.filter(
        (_, i) => i !== index,
      );

      // 시작 금액 자동 계산: 카테고리별 가격 중 가장 낮은 값
      const startPrice =
        updatedCategoryPrices.length > 0
          ? Math.min(
              ...updatedCategoryPrices.map((cp) => Number(cp.price) || 0),
            )
          : 0;

      return {
        ...prev,
        categoryPrices: updatedCategoryPrices,
        startPrice: startPrice,
      };
    });
  };

  const handleMediaChange = (mediaName) => {
    setFormData((prev) => {
      const currentMedia = prev.exposureMedia || [];
      const newMedia = currentMedia.includes(mediaName)
        ? currentMedia.filter((name) => name !== mediaName)
        : [...currentMedia, mediaName];
      return { ...prev, exposureMedia: newMedia };
    });
  };

  const handleUpdate = async () => {
    const updatedFields = {};
    for (const key in formData) {
      if (String(formData[key]) !== String(product[key])) {
        updatedFields[key] = formData[key];
      }
    }

    if (metaProduct === "news_release") {
      updatedFields.categoryPrices = formData.categoryPrices;
      updatedFields.exposureMedia = formData.exposureMedia;
      updatedFields.isGhostwriting = formData.isGhostwriting ? 1 : 0;
      delete updatedFields.exposurePosition;
    }

    if (metaProduct === "reward") {
      delete updatedFields.isGhostwriting;
      updatedFields.exposurePosition = formData.exposurePosition
        ? formData.exposurePosition.split(",")
        : [];
      updatedFields.maxPeriod = Number(formData.maxPeriod);
      updatedFields.minPeriod = Number(formData.minPeriod);
      updatedFields.minQuantity = Number(formData.minQuantity);
      updatedFields.price = Number(formData.price);
    }

    if (Object.keys(updatedFields).length === 0) {
      alert("변경된 내용이 없습니다.");
      return;
    }
    updatedFields.adProductId = Number(adProductId);

    try {
      const response = await defaultAxiosInstance.put(
        "/advertisement/admin/product/id",
        updatedFields,
      );
      if (response.data.status === 200) {
        alert("광고 상품 수정 완료");
        setIsEditing(false);
        fetchProductDetail();
      } else {
        alert("수정 실패");
      }
    } catch (error) {
      console.error("광고 상품 수정 오류:", error);
      alert("광고 상품 수정 오류");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("광고 상품을 삭제하시겠습니까?")) {
      try {
        const response = await defaultAxiosInstance.delete(
          "/advertisement/admin/product/id",
          {
            params: { adProductId },
          },
        );
        if (response.data.status === 200) {
          alert("광고 상품 삭제 완료");
          navigate("/ads/products");
        } else {
          alert("삭제 실패");
        }
      } catch (error) {
        console.error("광고 상품 삭제 오류:", error);
        alert("광고 상품 삭제 오류");
      }
    }
  };

  const renderFormFields = () => {
    switch (metaProduct) {
      case "reward":
        return (
          <>
            <div className="form-group">
              <label>시행사</label>
              <select
                name="advertiserName"
                value={formData.advertiserName}
                onChange={handleChange}
                disabled={!isEditing}
              >
                <option value="">선택</option>
                <option value="버즈빌">버즈빌</option>
                <option value="호올스">호올스</option>
                <option value="토스">토스</option>
                <option value="멘토스">멘토스</option>
                <option value="길찾기">길찾기</option>
                <option value="당근">당근</option>
                <option value="베이직">베이직</option>
                <option value="브라보">브라보</option>
                <option value="찰리">찰리</option>
                <option value="제주">제주</option>
                <option value="투플">투플</option>
                <option value="홈런볼">기프트</option>
                <option value="최적화">최적화</option>
                <option value="핫팩">핫팩</option>
                <option value="카카오">카카오</option>
                <option value="일키 트래픽">일키 트래픽</option>
                <option value="썬데이">썬데이</option>
                <option value="백링크">백링크</option>
                <option value="N4">N4</option>
                <option value="화이트">화이트</option>
              </select>
            </div>
            <div className="form-group">
              <label>상품명</label>
              <input
                type="text"
                name="adName"
                value={formData.adName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>상품 유형</label>
              <select
                name="productType"
                value={formData.productType}
                onChange={handleChange}
                disabled={!isEditing}
              >
                <option value="">선택</option>
                <option value="유입">유입</option>
                <option value="저장">저장</option>
                <option value="저장+유입">저장+유입</option>
              </select>
            </div>
            <div className="form-group">
              <label>평균 클릭 수</label>
              <input
                type="number"
                name="averageClicks"
                value={formData.averageClicks}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group full-width">
              <label>광고 설명</label>
              <textarea
                name="adDescription"
                value={formData.adDescription}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>가격</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>단위</label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                disabled={!isEditing}
              >
                <option value="">선택</option>
                <option value="건">건</option>
                <option value="월">월</option>
                <option value="년">년</option>
              </select>
            </div>
            <div className="form-group">
              <label>노출 매체 (쉼표로 구분)</label>
              <input
                type="text"
                name="exposureMedia"
                value={formData.exposureMedia}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>노출 구좌 (쉼표로 구분)</label>
              <input
                type="text"
                name="exposurePosition"
                value={formData.exposurePosition}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>최소 기간</label>
              <input
                type="number"
                name="minPeriod"
                value={formData.minPeriod}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>최대 기간</label>
              <input
                type="number"
                name="maxPeriod"
                value={formData.maxPeriod}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>최소 수량</label>
              <input
                type="number"
                name="minQuantity"
                value={formData.minQuantity}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
          </>
        );
      case "review_delete":
      case "blog_press_corps":
        return (
          <>
            <div className="form-group">
              <label>상품명</label>
              <input
                type="text"
                name="adName"
                value={formData.adName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group full-width">
              <label>광고 설명</label>
              <textarea
                name="adDescription"
                value={formData.adDescription}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>가격</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>단위</label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                disabled={!isEditing}
              >
                <option value="">선택</option>
                <option value="건">건</option>
                <option value="월">월</option>
                <option value="년">년</option>
              </select>
            </div>
          </>
        );
      case "news_release":
        return (
          <>
            <div className="form-group">
              <label>언론사 명</label>
              <input
                type="text"
                name="mediaName"
                value={formData.mediaName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>언론사 URL</label>
              <input
                type="text"
                name="mediaUrl"
                value={formData.mediaUrl}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>카테고리 및 가격</label>
              <div className="category-price-container">
                {formData.categoryPrices.map((item, index) => (
                  <div key={index} className="category-price-item">
                    <select
                      value={item.category?.categoryId || ""}
                      onChange={(e) =>
                        handleCategoryPriceChange(
                          index,
                          "categoryId",
                          e.target.value,
                        )
                      }
                      disabled={!isEditing}
                    >
                      <option value="">카테고리 선택</option>
                      {categoryOptions.map((category) => (
                        <option
                          key={category.categoryId}
                          value={category.categoryId}
                        >
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) =>
                        handleCategoryPriceChange(
                          index,
                          "price",
                          e.target.value,
                        )
                      }
                      disabled={!isEditing}
                      placeholder="가격"
                    />
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => handleRemoveCategoryPrice(index)}
                        className="remove-button"
                      >
                        -
                      </button>
                    )}
                  </div>
                ))}
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleAddCategoryPrice}
                    className="add-button"
                  >
                    +
                  </button>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>대필 여부</label>
              <select
                name="isGhostwriting"
                value={formData.isGhostwriting ? 1 : 0}
                onChange={handleChange}
                disabled={!isEditing}
              >
                <option value={0}>N</option>
                <option value={1}>Y</option>
              </select>
            </div>
            <div className="form-group">
              <label>송출 매체</label>
              <div className="media-checkbox-container">
                <div className="media-checkbox-item">
                  <input
                    type="checkbox"
                    id="media-naver"
                    checked={formData.exposureMedia.includes("네이버")}
                    onChange={() => handleMediaChange("네이버")}
                    disabled={!isEditing}
                  />
                  <label htmlFor="media-naver">네이버</label>
                </div>
                <div className="media-checkbox-item">
                  <input
                    type="checkbox"
                    id="media-daum"
                    checked={formData.exposureMedia.includes("다음")}
                    onChange={() => handleMediaChange("다음")}
                    disabled={!isEditing}
                  />
                  <label htmlFor="media-daum">다음</label>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>시작 금액</label>
              <input
                type="number"
                name="startPrice"
                value={formData.startPrice}
                readOnly
                disabled={!isEditing}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="ad-product-detail-container">
        <div className="detail-header">
          <h2>광고 상품 상세</h2>
          <div className="detail-button-group">
            {!isEditing ? (
              <>
                <button onClick={() => setIsEditing(true)}>수정 하기</button>
                <button onClick={handleDelete}>삭제 하기</button>
              </>
            ) : (
              <button onClick={handleUpdate}>저장 하기</button>
            )}
          </div>
        </div>
        <div className="detail-form">{renderFormFields()}</div>
      </div>
    </Layout>
  );
}

export default AdProductDetail;
